/**
 * Copyright (c) 2021-22 Amway
 * @author Mohit Chunawala
 */

import AppSyncClient from "aws-appsync";
import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  HttpLink,
  from,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { msalInstance } from "../core/authentication/entra/msal";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

// -------------------
// Base AppSync config
// -------------------
const appSyncConfig = {
  aws_appsync_graphqlEndpoint: process.env.REACT_APP_AWS_APPSYNC_BASE_URL!,
  aws_appsync_region: process.env.REACT_APP_AWS_APPSYNC_REGION!,
  aws_appsync_apiKey: process.env.REACT_APP_AWS_APPSYNC_API_KEY!,
};

// --------------------
// Admin token handling
// --------------------
const ADMIN_SCOPE = process.env.REACT_APP_ADMIN_API_SCOPE;

async function getAdminAccessToken(): Promise<string | null> {
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) return null;

    const tokenResp = await msalInstance.acquireTokenSilent({
      account: accounts[0],
      scopes: ADMIN_SCOPE ? [ADMIN_SCOPE] : [],
    });

    return tokenResp?.accessToken || null;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      console.warn("MSAL interaction required to get admin token");
    }
    return null;
  }
}

// ------------------------
// Apollo client for admin
// ------------------------
const httpLink = new HttpLink({
  uri: appSyncConfig.aws_appsync_graphqlEndpoint,
});

const authLink: ApolloLink = setContext(async (_, { headers }) => {
  const token = await getAdminAccessToken();
  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
});

const adminLink = from([authLink, httpLink]);

// ---------------------------------
// Switch based on current pathname
// ---------------------------------
const client = window.location.pathname.includes("/admin")
  ? new ApolloClient({
      link: adminLink, // Admin → Entra Bearer
      cache: new InMemoryCache(),
    })
  : new AppSyncClient({
      url: appSyncConfig.aws_appsync_graphqlEndpoint, // Consumer → API Key
      region: appSyncConfig.aws_appsync_region,
      auth: {
        type: "API_KEY",
        apiKey: appSyncConfig.aws_appsync_apiKey,
      },
    });

export default client;